import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, first } from 'rxjs/operators';

import { AuthService, UserService } from '@core/services';
import { NewsService, TimerAction } from '@core/services/news.service';
import { PromoTimerDialogComponent } from '@shared-v3/components/promo-timer-dialog/promo-timer-dialog.component';

/**
 * Оркестратор показа модального окна акций (карусель таймеров).
 *
 * Показывает {@link PromoTimerDialogComponent} один раз за полную загрузку
 * приложения: при первом завершённом `NavigationEnd` после того, как загружен
 * пользователь. Флаг «уже показано» in-memory — сбрасывается при перезагрузке
 * страницы (F5/перезапуск браузера) и переживает SPA-навигацию и bfcache.
 */
@Injectable({
  providedIn: 'root',
})
export class PromoTimersDialogService {
  /** Флаг «диалог уже показан в этой загрузке приложения». */
  private _isShownInThisLoad = false;

  constructor(
    private readonly _router: Router,
    private readonly _userService: UserService,
    private readonly _authService: AuthService,
    private readonly _newsService: NewsService,
    private readonly _dialog: MatDialog,
  ) {
    // loadedUser — BehaviorSubject: реплеит текущее значение позднему подписчику,
    // поэтому эмиссия не теряется, даже если пользователь загрузился до подписки.
    combineLatest([
      this._router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        first(),
      ),
      this._userService.loadedUser.pipe(first((user) => !!user)),
    ]).subscribe(() => this._tryShow());
  }

  /**
   * Попробовать показать диалог акций.
   */
  private _tryShow(): void {
    if (this._isShownInThisLoad) {
      return;
    }

    // Менеджерам промо-окна не показываем.
    if (this._authService.accountType === 'manager') {
      return;
    }

    const lang =
      this._userService.userInfo?.user?.language || localStorage.getItem('language') || 'ru';

    this._newsService
      .getTimerAction(lang)
      // Ошибка запроса — тихо пропускаем показ (флаг не ставим).
      .pipe(catchError(() => of([] as TimerAction[])))
      .subscribe((slides) => {
        if (!slides.length) {
          // Нечего показывать: флаг не ставим — при следующей полной загрузке проверим снова.
          return;
        }
        this._openWhenPossible(slides);
      });
  }

  /**
   * Открыть диалог, когда свободен MatDialog.
   *
   * Если уже открыт другой диалог (например, промо при логине) — ждём
   * `afterAllClosed` и повторяем попытку; ждём до тех пор, пока стек диалогов
   * не освободится (показ всё равно однократный — `_isShownInThisLoad`).
   */
  private _openWhenPossible(slides: TimerAction[]): void {
    if (this._isShownInThisLoad) {
      return;
    }

    if (this._dialog.openDialogs.length > 0) {
      this._dialog.afterAllClosed
        .pipe(first())
        .subscribe(() => this._openWhenPossible(slides));
      return;
    }

    this._dialog.open(PromoTimerDialogComponent, {
      hasBackdrop: true,
      backdropClass: 'blur-backdrop',
      width: '80vw',
      maxWidth: '800px',
      panelClass: 'promo-timer-dialog',
      data: { slides },
    });
    this._isShownInThisLoad = true;
  }
}
